/* eslint-disable no-await-in-loop */
import { useCampaignDetail } from 'data/campaign';
import _ from 'lodash';
import Papa, { ParseResult } from 'papaparse';
import React from 'react';

import { Box, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import Button from 'creative-components/CustomButtons/Button';

import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

import { updateCampaignEventOverride } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';
import { getFirstPrediction } from 'utils/places';
import { toast } from 'sonner';

const useStyles = makeStyles((theme) => ({
}));

const REQUIRED_HEADERS = [
  'Parcel Number',
  'Farming Enabled',
  'My Custom Owner First Name',
  'My Custom Owner Last Name',
  'My Custom Owner Email Address',
  'My Custom Owner Phone Number',
  'My Custom Owner Address1',
  'My Custom Owner Address2',
  'My Custom Owner City',
  'My Custom Owner State',
  'My Custom Owner Zip Code',
  'My Notes',
] as const;

type RequiredHeader = typeof REQUIRED_HEADERS[number];

// All headers MUST exist, values will always be string because PapaParse returns string data
type CsvRow = Record<RequiredHeader, string>;

interface Props {
  campaignId: string;
  handleExportToCsv: () => void;
  onClose: () => void;
}

const CampaignLeadsBulkEditModal: React.FC<Props> = ({ campaignId, handleExportToCsv, onClose }: Props) => {
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const { campaign, mutateCampaign } = useCampaignDetail(campaignId);

  const fileInput = React.createRef<HTMLInputElement>();

  const handleUpload = () => {
    if (fileInput.current) fileInput.current.value = '';
    fileInput.current?.click();
  };

  const onUploadCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const file = e.target.files?.[0];
    if (!file) return;

    showLoadingIndicatorModal();

    Papa.parse<Partial<CsvRow>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results: ParseResult<CsvRow>) => {
        try {
          if (results.errors.length > 0) {
            console.error(results.errors);
            toast.error('There was an error reading your CSV file.');
            return;
          }

          const fields = results.meta.fields ?? [];
          const missing = REQUIRED_HEADERS.filter((h) => !fields.includes(h));

          if (missing.length > 0) {
            toast.error(`Your CSV is missing required column(s): ${missing.join(', ')}.`);
            return;
          }

          // Validate at least one row exists
          if (results.data.length === 0) {
            toast.error('Your CSV file is empty.');
            return;
          }

          // Extract data (values are guaranteed string here)
          // Also convert the Farming Enabled column to a boolean
          const eventOverrideUpdates = [];
          const uniqueParcelNumbers = new Set<string>();
          for (let i = 0; i < results.data.length; i += 1) {
            const row = results.data[i];
            const parcelNumber = row['Parcel Number'];

            // Tracks if any updates are made and only creates an event
            // override if there are.
            let dirty = false;

            // Parse the "Farming Enabled" column
            let farmingStopped: boolean;
            if (row['Farming Enabled'] === 'true') {
              farmingStopped = false;
            } else if (row['Farming Enabled'] === 'false') {
              farmingStopped = true;
              dirty = true; // Don't set dirty flag for farmingStopped false
            } else {
              toast.error('The "Farming Enabled" column can only have "true" or "false" values.');
              return;
            }

            // Make sure the "Parcel Number" value is an APN of an event in this campaign
            const eventData = campaign.subscribedEvents.find((o: any) => o.apn === parcelNumber);

            if (!eventData) {
              toast.error(`The parcel number ${parcelNumber} doesn't exist in your campaign.`);
              return;
            }

            // Only one row per parcel number
            if (uniqueParcelNumbers.has(parcelNumber)) {
              toast.error(`The parcel number ${parcelNumber} has a duplicate row. Please provide only a single row for each parcel number.`);
              return;
            }
            uniqueParcelNumbers.add(parcelNumber);

            // Use original owner contact info as default values
            const ownerContact: any = {
              ..._.pick(eventData.ownerContact, ['firstName', 'lastName', 'email', 'phoneNumber']),
              address: {
                ..._.pick(eventData.ownerContact.address, ['address1', 'address2', 'city', 'state', 'zipcode']),
                location: {}, // Include this so AddressForm doesn't complain
              },
            };

            // Update the owner contact object only if a value was provided
            if (row['My Custom Owner First Name']) {
              dirty = true;
              ownerContact.firstName = row['My Custom Owner First Name'];
            }
            if (row['My Custom Owner Last Name']) {
              dirty = true;
              ownerContact.lastName = row['My Custom Owner Last Name'];
            }
            if (row['My Custom Owner Phone Number']) {
              dirty = true;
              ownerContact.phoneNumber = row['My Custom Owner Phone Number'];
            }
            if (row['My Custom Owner Email Address']) {
              dirty = true;
              ownerContact.email = row['My Custom Owner Email Address'];
            }

            // If they provided an address, look it up to replace. Otherwise, reset it
            if (row['My Custom Owner Address1']) {
              // Sanity check to make sure they provided all components of the address
              if (!row['My Custom Owner City'] || !row['My Custom Owner State'] || !row['My Custom Owner Zip Code']) {
                toast.error(`You must provide all the address components for the row with parcel number ${parcelNumber}.`);
                return;
              }

              // Look up the address using the Places Autocomplete API
              const addressInput = [
                row['My Custom Owner Address1'],
                row['My Custom Owner Address2'],
                row['My Custom Owner City'],
                row['My Custom Owner State'],
                row['My Custom Owner Zip Code'],
              ].join(' ').trim();
              const address = await getFirstPrediction(addressInput);

              if (!address) {
                toast.error(`We couldn't lookup the address ${row['My Custom Owner Address1']} for parcel number ${row['Parcel Number']}.`);
                return;
              }

              dirty = true;
              ownerContact.address = _.pick(address, 'address1', 'address2', 'city', 'state', 'zipcode', 'location');
            }

            if (row['My Notes']) dirty = true;

            const existingEventOverride = campaign.eventOverrides.find((o: any) => o.event === eventData._id);

            if (dirty || existingEventOverride) {
              eventOverrideUpdates.push({
                eventId: eventData._id,
                ownerContact,
                farmingStopped,
                notes: row['My Notes'],
              });
            }
          }

          // Once all data has been validated, go back through it and now do the API calls one by one
          for (let i = 0; i < eventOverrideUpdates.length; i += 1) {
            const eventOverrideUpdate = eventOverrideUpdates[i];

            await updateCampaignEventOverride(
              campaignId,
              eventOverrideUpdate.eventId,
              eventOverrideUpdate.ownerContact,
              eventOverrideUpdate.farmingStopped,
              eventOverrideUpdate.notes,
            );
          }

          // Refetch campaign to update monthly cost and override data
          await mutateCampaign();

          onClose();

          toast.success('Your bulk edits have been saved.');
        } catch (err) {
          console.error(err);
          showAPIErrorAlert(err);
        } finally {
          hideLoadingIndicatorModal();
        }
      },
    });
  };

  if (!campaign) return null;

  return (
    <Dialog
      open
      scroll="body"
      keepMounted
      maxWidth="md"
      onClose={onClose}
    >
      <SimpleModalHeader onClose={onClose} title="Prospect Bulk Edit" />

      <Typography component="p">
        You can bulk edit your prospects by exporting and uploading a CSV file.
        <ol>
          <li>Click Export Existing (bottom left) to download your current prospect list.</li>
          <li>Open the CSV, make your updates in the editable columns listed below.</li>
          <li>Save the file and click Upload CSV to apply your changes.</li>
        </ol>
        <br />
        <strong>Editable columns:</strong>
        <ul style={{ fontStyle: 'italic' }}>
          <li>Farming Enabled</li>
          <li>My Custom Owner First Name</li>
          <li>My Custom Owner Last Name</li>
          <li>My Custom Owner Email Address</li>
          <li>My Custom Owner Phone Number</li>
          <li>My Custom Owner Address1</li>
          <li>My Custom Owner Address2</li>
          <li>My Custom Owner City</li>
          <li>My Custom Owner State</li>
          <li>My Custom Owner Zip Code</li>
          <li>My Notes</li>
        </ul>
      </Typography>

      <Box display="flex" justifyContent="space-between" sx={{ mt: 8 }}>
        <Button
          round
          color="dark"
          onClick={handleExportToCsv}
        >
          <FileCopyIcon />
          Export Existing
        </Button>

        <>
          <input
            type="file"
            accept="text/csv"
            onChange={onUploadCsv}
            ref={fileInput}
            style={{ display: 'none' }}
          />
          <Button
            color="primary"
            round
            onClick={handleUpload}
          >
            <CloudUploadIcon />
            Upload CSV
          </Button>
        </>
      </Box>
    </Dialog>
  );
};

export default CampaignLeadsBulkEditModal;
